import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';

interface Profile {
  id: string;
  email: string;
  full_name: string | null;
  avatar_url: string | null;
  phone: string | null;
  employee_id: string | null;
  department: string | null;
  job_title: string | null;
  is_super_admin: boolean;
}

interface AuthContextType {
  user: User | null;
  session: Session | null;
  profile: Profile | null;
  loading: boolean;
  profileLoading: boolean;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signUp: (email: string, password: string, fullName: string) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
  signInWithMicrosoft: () => Promise<{ error: Error | null }>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);
  const [profileLoading, setProfileLoading] = useState(true);
  const profileRequestRef = useRef<Promise<Profile | null> | null>(null);
  const lastProfileIdRef = useRef<string | null>(null);

  const fetchProfile = async (userId: string) => {
    const { data, error } = await supabase
      .schema('core')
      .from('profiles')
      .select('id, email, full_name, avatar_url, phone, employee_id, department, job_title, is_super_admin')
      .eq('id', userId)
      .maybeSingle();
    
    if (!error && data) {
      setProfile(data as Profile);
      return data as Profile;
    }
    setProfile(null);
    return null;
  };

  const loadProfile = (userId: string) => {
    if (lastProfileIdRef.current === userId && profile) {
      return Promise.resolve(profile);
    }

    if (profileRequestRef.current) {
      return profileRequestRef.current;
    }

    lastProfileIdRef.current = userId;
    setProfileLoading(true);
    const request = fetchProfile(userId).finally(() => {
      profileRequestRef.current = null;
      setProfileLoading(false);
    });
    profileRequestRef.current = request;
    return request;
  };

  // Track the current access token to avoid redundant state updates
  // when Supabase fires TOKEN_REFRESHED / SIGNED_IN on tab visibility change
  const lastAccessTokenRef = useRef<string | null>(null);

  useEffect(() => {
    // Set up auth state listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, newSession) => {
        // On token refresh or re-sign-in (e.g. tab becomes visible),
        // skip state updates if the session hasn't actually changed.
        // This prevents a full component tree re-render that unmounts pages.
        if (
          (event === 'TOKEN_REFRESHED' || event === 'SIGNED_IN') &&
          newSession?.access_token &&
          lastAccessTokenRef.current === newSession.access_token
        ) {
          return;
        }
        lastAccessTokenRef.current = newSession?.access_token ?? null;

        setSession(newSession);
        setUser(newSession?.user ?? null);
        
        // Defer profile fetch
        if (newSession?.user) {
          setTimeout(() => {
            loadProfile(newSession.user.id);
          }, 0);
        } else {
          setProfile(null);
          setProfileLoading(false);
        }
      }
    );

    // THEN check for existing session
    supabase.auth.getSession().then(({ data: { session: existingSession } }) => {
      lastAccessTokenRef.current = existingSession?.access_token ?? null;
      setSession(existingSession);
      setUser(existingSession?.user ?? null);
      if (existingSession?.user) {
        loadProfile(existingSession.user.id);
      } else {
        setProfileLoading(false);
      }
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    return { error: error as Error | null };
  };

  const signUp = async (email: string, password: string, fullName: string) => {
    // Redirect to email-confirmation page after clicking the confirmation link
    const redirectUrl = `${window.location.origin}/email-confirmation`;
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: redirectUrl,
        data: { full_name: fullName }
      }
    });
    return { error: error as Error | null };
  };

  const signOut = async () => {
    const { error } = await supabase.auth.signOut({ scope: 'global' });
    if (error) {
      console.error('Sign out failed', error);
    }
    try {
      const storageKey = (supabase.auth as unknown as { storageKey?: string }).storageKey;
      if (storageKey) {
        localStorage.removeItem(storageKey);
      }
    } catch (storageError) {
      console.error('Failed to clear auth storage', storageError);
    }
    setSession(null);
    setUser(null);
    setProfile(null);
    setProfileLoading(false);
  };

  const signInWithMicrosoft = async () => {
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'azure',
      options: {
        scopes: 'email profile openid',
        redirectTo: `${window.location.origin}/`
      }
    });
    return { error: error as Error | null };
  };

  return (
    <AuthContext.Provider value={{ user, session, profile, loading, profileLoading, signIn, signUp, signOut, signInWithMicrosoft }}>
      {children}
    </AuthContext.Provider>
  );
};
